import { type SagaIterator } from 'redux-saga';
import { call, delay, put, select } from 'redux-saga/effects';

import { CopilotTrackEvent } from 'constants/copilot-event';
import { QueryKey } from 'constants/query-key';
import { EventPlace } from 'helpers/analytics';
import { navigate, openInNewCard } from 'helpers/routing';
import { uniqueId } from 'helpers/string';
import { type KnowledgeSourceItem } from 'interfaces/knowledge-sources';
import { trackEvent } from 'services/event-tracking';
import { getQueryClient } from 'services/query-client/client';
import { getLoggedInAgentName } from 'store/entities/agents/selectors';
import { AgentCustomPropertyName } from 'store/features/agent-custom-properties/interfaces';
import { getAgentCustomProperty } from 'store/features/agent-custom-properties/selectors';
import { type IActionWithPayload } from 'store/helper';
import { CopilotViewActions } from 'store/views/copilot/actions';
import { getCopilotIsLoading } from 'store/views/copilot/selectors';

import { CopilotEntitiesActions } from '../actions';
import { setUnreadMessagesCount } from '../helper-sagas';
import { type CopilotNavigateActionPayload } from '../interfaces';

import { COPILOT_ONBOARDING_PRIORITY } from './constants';
import { type OnboardingConfig } from './copilot-onboarding-config';

export function* getOnboardingToStart(): SagaIterator {
  const isCopilotTyping = yield select(getCopilotIsLoading);

  if (isCopilotTyping) {
    return null;
  }

  const hasSeenCopilotOnboarding: boolean = yield select(
    getAgentCustomProperty,
    AgentCustomPropertyName.OnePopoverOnboardingSeen,
  );

  return COPILOT_ONBOARDING_PRIORITY.find((onboardingName) => {
    switch (onboardingName) {
      case 'copilot-onboarding':
        return !hasSeenCopilotOnboarding;
      default:
        return null;
    }
  });
}

export function* handleCopilotNavigateAction(
  action: IActionWithPayload<string, CopilotNavigateActionPayload>,
): SagaIterator {
  const { content, props } = action.payload;

  yield put(
    CopilotEntitiesActions.addCopilotMessage({
      authorId: 'agent',
      authorType: 'agent',
      eventId: uniqueId(),
      text: content,
      timestampInMs: Date.now(),
      type: 'text-message',
    }),
  );
  if (props.url) {
    if (props.isExternal) {
      openInNewCard(props.url);

      return;
    }
    navigate(props.url);
  }

  if (props.delay) {
    yield put(CopilotViewActions.setCopilotIsLoading(true));
    yield delay(props.delay);
    yield put(CopilotViewActions.setCopilotIsLoading(false));
  }

  switch (props.responseType) {
    case 'final-message':
      yield put(
        CopilotEntitiesActions.addCopilotMessage({
          authorId: 'live-assistant',
          authorType: 'live-assistant',
          eventId: uniqueId(),
          text: `I’m here if you need any help! 👍`,
          timestampInMs: Date.now(),
          type: 'text-message',
          properties: {
            withoutActions: true,
          },
        }),
      );
      break;
  }

  yield call(setUnreadMessagesCount);
}

export function* startCopilotOnboarding(config: OnboardingConfig): SagaIterator {
  trackEvent(CopilotTrackEvent.Onboarding, EventPlace.One, { step: 'Onboarding started' });

  const loggedAgentName: string = yield select(getLoggedInAgentName);

  const queryClient = getQueryClient();
  const knowledgeSources = queryClient.getQueryData<KnowledgeSourceItem[]>([QueryKey.KnowledgeSourcesList]);
  const hasAnyKnowledgeSources = !!knowledgeSources?.length;

  for (const getMessage of config) {
    const message = getMessage({ name: loggedAgentName, hasAnyKnowledgeSources });

    if (!message) {
      continue;
    }

    yield put(
      CopilotEntitiesActions.addCopilotMessage({
        authorId: 'live-assistant',
        authorType: 'live-assistant',
        eventId: uniqueId(),
        text: message.text,
        timestampInMs: Date.now(),
        type: 'text-message',
        properties: {
          buttons: message.buttons ?? undefined,
          withoutActions: true,
          isOnboarding: true,
        },
      }),
    );

    if (message.delay) {
      yield put(CopilotViewActions.setCopilotIsLoading(true));
      yield delay(message.delay);
      yield put(CopilotViewActions.setCopilotIsLoading(false));
    }

    yield call(setUnreadMessagesCount);
  }
  trackEvent(CopilotTrackEvent.Onboarding, EventPlace.One, { step: 'Onboarding finished' });
}
